import { useState, useEffect, useRef } from "react";
import { Shield, Plus, Pencil, Trash2, Upload, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { getTeams, createTeam, updateTeam, deleteTeam, getMatches } from "../lib/api";
import { toast } from "sonner";

export default function Teams() {
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [formData, setFormData] = useState({ name: "", logo: "" });
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [teamsRes, matchesRes] = await Promise.all([
        getTeams(),
        getMatches()
      ]);
      setTeams(teamsRes.data);
      setMatches(matchesRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await getTeams();
      setTeams(response.data);
    } catch (error) {
      toast.error("Failed to fetch teams");
    }
  };

  // Get last 5 completed matches for a team
  const getTeamLast5 = (teamId) => {
    const teamMatches = matches
      .filter(m => 
        m.status === "completed" && 
        (m.home_team_id === teamId || m.away_team_id === teamId)
      )
      .sort((a, b) => {
        const dateA = new Date(`${a.match_date}T${a.match_time || "00:00"}`);
        const dateB = new Date(`${b.match_date}T${b.match_time || "00:00"}`);
        return dateB - dateA; // Most recent first
      })
      .slice(0, 5);
    
    return teamMatches.map(match => {
      const isHome = match.home_team_id === teamId;
      const teamScore = isHome ? match.home_score : match.away_score;
      const oppScore = isHome ? match.away_score : match.home_score;
      const oppTeamId = isHome ? match.away_team_id : match.home_team_id;
      const oppTeam = teams.find(t => t.id === oppTeamId);
      
      let result = "D";
      if (teamScore > oppScore) result = "W";
      else if (teamScore < oppScore) result = "L";
      
      return {
        result,
        teamScore,
        oppScore,
        oppName: oppTeam?.name || "Unknown",
        isHome
      };
    });
  };

  const handleOpenDialog = (team = null) => {
    if (team) {
      setSelectedTeam(team);
      setFormData({ name: team.name, logo: team.logo || "" });
    } else {
      setSelectedTeam(null);
      setFormData({ name: "", logo: "" });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTeam(null);
    setFormData({ name: "", logo: "" });
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Image must be less than 2MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({ ...prev, logo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Team name is required");
      return;
    }

    setSaving(true);
    try {
      if (selectedTeam) {
        await updateTeam(selectedTeam.id, formData);
        toast.success("Team updated successfully");
      } else {
        await createTeam(formData);
        toast.success("Team created successfully");
      }
      handleCloseDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save team");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTeam) return;

    try {
      await deleteTeam(selectedTeam.id);
      toast.success("Team deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedTeam(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete team");
    }
  };

  const openDeleteDialog = (team) => {
    setSelectedTeam(team);
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="teams-page" className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold uppercase tracking-tight">
            Teams
          </h1>
          <p className="text-muted-foreground mt-2">
            Manage tournament teams ({teams.length}/6)
          </p>
        </div>
        {teams.length < 6 && (
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider btn-glow"
            data-testid="add-team-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Team
          </Button>
        )}
      </div>

      {/* Teams Grid */}
      {teams.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {teams.map((team, index) => {
            const last5 = getTeamLast5(team.id);
            return (
              <div
                key={team.id}
                className="bg-card border border-border rounded-xl overflow-hidden card-hover animate-fade-in opacity-0"
                style={{ animationDelay: `${index * 50}ms` }}
                data-testid={`team-card-${team.id}`}
              >
                <div className="aspect-video bg-secondary relative flex items-center justify-center">
                  {team.logo ? (
                    <img
                      src={team.logo}
                      alt={team.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Shield className="w-20 h-20 text-muted-foreground/30" />
                  )}
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-bold uppercase tracking-wider mb-3">
                    {team.name}
                  </h3>
                  
                  {/* Last 5 Results */}
                  <div className="mb-4">
                    <p className="text-xs text-muted-foreground uppercase font-semibold mb-2">Last 5 Results</p>
                    {last5.length > 0 ? (
                      <div className="space-y-1.5">
                        {last5.map((match, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              <span className={`w-5 h-5 flex items-center justify-center rounded text-xs font-bold ${
                                match.result === "W" ? "bg-green-500/20 text-green-400" :
                                match.result === "L" ? "bg-red-500/20 text-red-400" :
                                "bg-gray-500/20 text-gray-400"
                              }`}>
                                {match.result}
                              </span>
                              <span className="text-muted-foreground text-xs truncate max-w-[100px]">
                                {match.isHome ? "vs" : "@"} {match.oppName}
                              </span>
                            </div>
                            <span className="font-mono font-semibold text-xs">
                              {match.teamScore} - {match.oppScore}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground italic">No matches played yet</p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleOpenDialog(team)}
                      className="flex-1"
                      data-testid={`edit-team-${team.id}`}
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openDeleteDialog(team)}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      data-testid={`delete-team-${team.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <Shield className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-bold uppercase mb-2">
            No Teams Yet
          </h3>
          <p className="text-muted-foreground mb-6">
            Add your first team to get started with the tournament
          </p>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider btn-glow"
            data-testid="empty-add-team-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add First Team
          </Button>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="bg-card border-border sm:max-w-md"
          data-testid="team-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold uppercase tracking-wider">
              {selectedTeam ? "Edit Team" : "Add Team"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Team Name
              </Label>
              <Input
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Enter team name"
                className="bg-background border-border"
                data-testid="team-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Team Logo
              </Label>
              <div className="space-y-4">
                {formData.logo && (
                  <div className="relative w-32 h-32 mx-auto">
                    <img
                      src={formData.logo}
                      alt="Logo preview"
                      className="w-full h-full object-cover rounded-xl border border-border"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, logo: "" }))
                      }
                      className="absolute -top-2 -right-2 w-6 h-6 bg-destructive text-white rounded-full flex items-center justify-center"
                      data-testid="remove-logo-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full"
                  data-testid="upload-logo-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {formData.logo ? "Change Logo" : "Upload Logo"}
                </Button>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={handleCloseDialog}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider"
                data-testid="save-team-btn"
              >
                {saving ? "Saving..." : selectedTeam ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl font-bold uppercase">
              Delete Team
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedTeam?.name}? This will
              also delete all players and matches associated with this team.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-team-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
